import { GreenhouseCustomFieldId, GreenhouseJob, JOB_ID_SEPARATOR } from './greenhouse';
import { hasConsent, ONETRUST_CATEGORIES } from 'libs/oneTrust';
import { SAVED_JOBS_COOKIE } from '@/utils/cookies';
import { slugify } from '@/utils/formatters';

export const getJobsByGreenhouseField = (jobs: GreenhouseJob[], fieldId: GreenhouseCustomFieldId) => {
  return jobs.filter(job => job.metadata?.find(metadata => metadata.id === fieldId)?.value === true);
};

export const getJobTeam = (job: GreenhouseJob): string | null => {
  const value = job.metadata?.find(metadata => metadata.id === GreenhouseCustomFieldId.Team)?.value;
  // Narrows an unparsed value to a primitive before using it.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  return typeof value === 'string' && value.length > 0 ? value : null;
};

export const getJobUrl = (job: GreenhouseJob): string => {
  return `/jobs/${job.id}/${slugify(job.title)}`;
};

export const getJobLocations = (jobs: GreenhouseJob[]): string[] =>
  [
    ...new Set(
      jobs.flatMap(job =>
        job.location.name
          .split(';')
          .map(loc => loc.trim())
          .filter(Boolean),
      ),
    ),
  ].sort((a, b) => a.localeCompare(b));

export const getJobTeams = (jobs: GreenhouseJob[]): string[] =>
  [
    ...new Set(
      jobs.flatMap(job => {
        const team = getJobTeam(job);
        return team !== null ? [team] : [];
      }),
    ),
  ].sort((a, b) => a.localeCompare(b));

export interface GreenhouseJobFilters {
  keyword?: string;
  locations?: string[];
  teams?: string[];
  remoteOnly?: boolean;
}

export const filterGreenhouseJobs = (
  jobs: GreenhouseJob[],
  { keyword = '', locations = [], teams = [], remoteOnly = false }: GreenhouseJobFilters,
): GreenhouseJob[] => {
  const query = keyword.trim().toLowerCase();

  return jobs.filter(job => {
    const matchesKeyword =
      !query || job.title.toLowerCase().includes(query) || job.content?.toLowerCase().includes(query);

    // Greenhouse joins a job's multiple locations into one string with ';'.
    const jobLocations = job.location.name.split(';').map(loc => loc.trim());
    const matchesLocation = locations.length === 0 || jobLocations.some(loc => locations.includes(loc));

    const matchesTeam = teams.length === 0 || teams.includes(getJobTeam(job) ?? '');

    const isRemoteJob = job.metadata?.find(m => m.id === GreenhouseCustomFieldId.OpenToRemote)?.value === true;
    const matchesRemote = !remoteOnly || isRemoteJob;

    return matchesKeyword && matchesLocation && matchesTeam && matchesRemote;
  });
};

let lastCookieString = '';
let cachedIds: number[] = [];

function getSavedJobsCookieValue(): string {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof document === 'undefined') return '';
  return (
    document.cookie
      .split('; ')
      .find(row => row.startsWith(`${SAVED_JOBS_COOKIE}=`))
      ?.split('=')[1] || ''
  );
}

function parseSavedJobIds(cookieValue: string): number[] {
  return cookieValue ? cookieValue.split(JOB_ID_SEPARATOR).filter(Boolean).map(Number) : [];
}

export function setSavedJobsCookie(ids: number[]): void {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof document === 'undefined') return;
  if (!hasConsent(ONETRUST_CATEGORIES.FUNCTIONAL)) return;

  const value = ids.length ? ids.join(JOB_ID_SEPARATOR) : '';
  document.cookie = `${SAVED_JOBS_COOKIE}=${value}; path=/; max-age=31536000; SameSite=Lax`;
}

export const getSavedJobIds = (): number[] => {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof document === 'undefined') return [];

  const currentCookie = getSavedJobsCookieValue();
  if (currentCookie !== lastCookieString) {
    lastCookieString = currentCookie;
    cachedIds = parseSavedJobIds(currentCookie);
  }
  return cachedIds;
};

export const pruneSavedJobIds = (existingJobIds: number[]): boolean => {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof document === 'undefined') return false;

  const cookieValue = getSavedJobsCookieValue();
  const savedIds = parseSavedJobIds(cookieValue);
  const prunedIds = savedIds.filter(id => existingJobIds.includes(id));
  if (prunedIds.length === savedIds.length) return false;

  setSavedJobsCookie(prunedIds);
  lastCookieString = '';
  cachedIds = [];
  return true;
};
